import dayjs from 'dayjs';

/**
 * 세팅 과제 카드의 **체크·가리기 상태** — 이 브라우저에만 저장한다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 서버가 아니라 localStorage 인가
 * ─────────────────────────────────────────────────────────────
 * 세팅 과제는 **서버 브리핑 항목이 아니다.** 웹이 온보딩·정착 집계로 합성해
 * 만드는 카드라, 서버에 「이 항목을 처리했다」고 보낼 대상 자체가 없다
 * (`itemId` 가 없으니 `PATCH /items/:itemId` 를 부를 수 없다).
 *
 * 그렇다고 체크를 못 하게 두면 신규 센터의 진행률이 영원히 0/N 이다 —
 * 그 센터에는 회원 카드가 아예 없어서 채울 것이 세팅 과제뿐이기 때문이다.
 * 그래서 **이 브라우저에서만 기억하는** 절충으로 간다.
 *
 * ⚠️ **한계를 화면이 밝힌다.** 다른 기기·다른 브라우저에서는 다시 뜬다.
 *    원장님이 집 PC 에서 체크하고 센터 PC 에서 그대로 뜨는 것을 보면
 *    「저장이 안 된다」로 읽으므로, 카드와 확인 창이 그 사실을 적는다.
 *
 * 🔴 **다음 단계는 서버화다.** 서버가 세팅 과제를 정식 브리핑 항목으로
 *    만들어 주면(= `itemId` 가 생기면) 회원 카드와 **같은 경로**로 옮긴다 —
 *    체크는 `ACTED`, 가리기는 `DISMISSED`. 그때 이 파일은 통째로 지운다.
 *    지울 때 마이그레이션은 필요 없다(하루짜리 상태라 다음 날 사라진다).
 */

const CHECK_KEY = 'opsSetupCheckedV1';
const HIDE_KEY = 'opsSetupHiddenV1';

/**
 * 가린 과제를 며칠 동안 안 보여줄 것인가.
 *
 * 회원 카드 가리기와 **같은 의미론**이다 — 서버가 회원 항목을 3일 억제한다
 * (`composition.suppressedCandidates`). 화면 문구도 같은 말을 쓴다:
 * 「사흘간 다시 알리지 않습니다」. 두 카드가 같은 버튼으로 다른 기간을 쓰면
 * 원장님은 어느 쪽이 진짜인지 모른다.
 */
export const SETUP_HIDE_DAYS = 3;

/** 기기별 저장이라는 사실 — 카드 툴팁·확인 창이 **그대로** 쓴다 */
export const SETUP_LOCAL_ONLY_NOTE = '이 브라우저에서만 기억됩니다';

const todayKey = (today: Date = new Date()) => dayjs(today).format('YYYY-MM-DD');

/**
 * 저장 실패는 **삼킨다.**
 *
 * 시크릿 창·저장 공간 부족·브라우저 설정으로 localStorage 가 통째로 던지는
 * 환경이 있다. 거기서 화면이 죽으면 세팅 과제를 아예 못 본다 —
 * 체크가 안 되는 것보다 훨씬 나쁘다. **없으면 「표시」가 기본**이다.
 */
const read = <T>(key: string, fallback: T): T => {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        /*
         * 🔴 **모양까지 확인한다.** `JSON.parse('null')` 은 던지지 않고 `null` 을
         *    돌려주는데, 그걸 그대로 쓰면 `Object.entries(null)` 에서 터진다.
         *    저장된 값은 우리가 쓴 것만 있는 게 아니다 — 옛 버전·다른 탭·손으로
         *    고친 값이 들어 있을 수 있고, 그때 화면이 죽으면 과제를 아예 못 본다.
         */
        if (parsed == null || typeof parsed !== 'object') return fallback;
        return parsed as T;
    } catch (e) {
        return fallback;
    }
};

const write = (key: string, value: unknown): void => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        /* 저장 못 해도 화면은 그대로 돈다 — 새로고침하면 되돌아갈 뿐이다 */
    }
};

/* ═══════════════════════════════════════════════════════════
   체크 — 그날 하루짜리
   ═══════════════════════════════════════════════════════════ */

type CheckedStore = { date: string; ids: string[] };

/**
 * 오늘 체크한 과제들.
 *
 * 🔴 **날짜가 다르면 빈 집합이다.** 어제 체크한 것이 오늘까지 남으면
 *    「오늘 할 일을 다 했다」는 화면이 매일 아침 거짓으로 뜬다.
 *    회원 카드의 완료(ACTED)가 그날 브리핑 문서에 매인 것과 같은 규칙이다.
 */
export const readCheckedSetupTasks = (today: Date = new Date()): Set<string> => {
    const store = read<CheckedStore | null>(CHECK_KEY, null);
    if (!store || store.date !== todayKey(today)) return new Set();
    return new Set(Array.isArray(store.ids) ? store.ids : []);
};

export const toggleCheckedSetupTask = (
    id: string,
    today: Date = new Date()
): Set<string> => {
    const next = readCheckedSetupTasks(today);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    write(CHECK_KEY, { date: todayKey(today), ids: Array.from(next) } satisfies CheckedStore);
    return next;
};

/* ═══════════════════════════════════════════════════════════
   가리기 — 사흘 억제
   ═══════════════════════════════════════════════════════════ */

type HiddenStore = Record<string, string>;

/**
 * 지금 가려져 있는 과제들.
 *
 * 만료된 기록은 읽을 때 걸러낸다 — 저장소를 청소하려고 쓰기를 하지 않는다.
 * 읽기만 하는 경로에서 쓰기를 하면, 여러 탭이 열려 있을 때 서로의 상태를
 * 덮어쓴다.
 */
export const readHiddenSetupTasks = (today: Date = new Date()): Set<string> => {
    const store = read<HiddenStore>(HIDE_KEY, {});
    const out = new Set<string>();
    const now = dayjs(today).startOf('day');
    Object.entries(store).forEach(([id, at]) => {
        const since = dayjs(at);
        if (!since.isValid()) return;
        if (now.diff(since.startOf('day'), 'day') < SETUP_HIDE_DAYS) out.add(id);
    });
    return out;
};

export const hideSetupTask = (id: string, today: Date = new Date()): Set<string> => {
    const store = read<HiddenStore>(HIDE_KEY, {});
    write(HIDE_KEY, { ...store, [id]: todayKey(today) });
    return readHiddenSetupTasks(today);
};

/** 방금 가린 것을 되돌린다 — 토스트의 「되돌리기」가 쓴다 */
export const unhideSetupTask = (id: string, today: Date = new Date()): Set<string> => {
    const store = read<HiddenStore>(HIDE_KEY, {});
    delete store[id];
    write(HIDE_KEY, store);
    return readHiddenSetupTasks(today);
};
